/**
 * Spec Phase 9 Slice 9B — propose-revision modal.
 *
 * Single component, two paths (claim + drawing) parameterised
 * by the ``target`` prop. Submitting POSTs to
 * /spec/revision-proposals; on success the parent navigates the
 * user to the redirect URL (Stage 3 / Stage 5) where the actual
 * edit happens.
 *
 * Validation:
 *   - proposed_change_text required (Submit disabled until non-blank)
 *   - rationale optional
 *
 * The architectural rule (NO claims-side or drawing-side mutation
 * from Spec) lives in the backend; this component just gathers
 * input + submits.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';

export interface ProposeRevisionTargetClaim {
  kind: 'claim';
  claim_id: string;
  claim_number: number;
  claim_text: string;
}

export interface ProposeRevisionTargetDrawing {
  kind: 'drawing';
  drawing_figure_id: string;
  fig_label: string | null;
  summary_short?: string;
}

export type ProposeRevisionTarget =
  | ProposeRevisionTargetClaim
  | ProposeRevisionTargetDrawing;

interface Props {
  target: ProposeRevisionTarget;
  onClose: () => void;
}

export default function ProposeRevisionModal({
  target,
  onClose,
}: Props): JSX.Element {
  const submit = useSpecStore((s) => s.submitRevisionProposal);
  const submitStatus = useSpecStore((s) => s.proposalSubmitStatus);
  const showToast = useSpecStore((s) => s.showToast);

  const [proposedChangeText, setProposedChangeText] = useState('');
  const [rationale, setRationale] = useState('');
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Autofocus the proposed-change textarea on mount per brief §5
  // ("[textarea, 6 lines, placeholder: …]" — primary input).
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Esc closes the modal — common modal-keyboard convention.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const trimmed = proposedChangeText.trim();
  const isSubmitting = submitStatus === 'submitting';
  const canSubmit = trimmed.length > 0 && !isSubmitting;

  const titleText = target.kind === 'claim'
    ? `Propose revision to Claim ${target.claim_number}`
    : `Propose revision to ${target.fig_label ?? 'Untitled'}`;

  const targetCtaText = target.kind === 'claim'
    ? 'Submit and go to Stage 3 →'
    : 'Submit and go to Stage 5 →';

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    setError(null);
    const body = target.kind === 'claim'
      ? {
          target_kind: 'claim' as const,
          target_claim_id: target.claim_id,
          proposed_change_text: trimmed,
          rationale: rationale.trim() || null,
        }
      : {
          target_kind: 'drawing' as const,
          target_drawing_figure_id: target.drawing_figure_id,
          proposed_change_text: trimmed,
          rationale: rationale.trim() || null,
        };

    const result = await submit(body);
    if (result.kind === 'ok') {
      const stageLabel = target.kind === 'claim' ? 'Stage 3' : 'Stage 5';
      showToast(`Proposal sent · redirecting to ${stageLabel}`);
      // Defer the navigation a tick so the toast renders + the
      // close animation can settle.
      window.setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.location.assign(result.redirectUrl);
        }
      }, 250);
      onClose();
    } else if (result.kind === 'locked') {
      setError('Specification locked — drawings unfrozen mid-flow.');
    } else {
      // 400 target_not_frozen / target_locator_mismatch land here.
      setError(result.message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-zinc-900/40 px-4"
      role="dialog"
      aria-modal="true"
      data-testid="spec-propose-revision-modal"
      onClick={(e) => {
        // Click outside the panel closes (matches OverwriteConfirmDialog).
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-md border border-zinc-300 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-2">
          <h3 className="text-sm font-medium text-zinc-800">{titleText}</h3>
          <button
            type="button"
            className="rounded-sm p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
            data-testid="spec-propose-revision-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 px-4 py-3">
          {target.kind === 'claim' && (
            <div className="rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-xs text-zinc-700">
              <div className="text-[10px] font-mono text-zinc-500">
                Current text (read-only)
              </div>
              <p className="mt-1 line-clamp-6 leading-relaxed">
                {target.claim_text}
              </p>
            </div>
          )}
          {target.kind === 'drawing' && (
            <div className="rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-xs text-zinc-700">
              <div className="text-[10px] font-mono text-zinc-500">
                {target.fig_label ?? 'Untitled figure'}
              </div>
              {target.summary_short && (
                <p className="mt-1 line-clamp-3 leading-relaxed">
                  {target.summary_short}
                </p>
              )}
            </div>
          )}

          <div>
            <label
              className="block text-[11px] font-medium text-zinc-700"
              htmlFor="spec-propose-change"
            >
              Your proposed change <span className="text-rose-600">*</span>
            </label>
            <textarea
              id="spec-propose-change"
              ref={textareaRef}
              className="mt-1 w-full rounded-sm border border-zinc-300 bg-white px-2 py-1.5 text-xs text-zinc-800 outline-none focus:border-zinc-500"
              rows={6}
              placeholder={
                target.kind === 'claim'
                  ? "Describe what should change. e.g. \"Replace 'sensor array' with 'temperature sensor array' throughout claim 1.\""
                  : "Describe what should change. e.g. \"Add label '14' for the actuator.\""
              }
              value={proposedChangeText}
              onChange={(e) => setProposedChangeText(e.target.value)}
              disabled={isSubmitting}
              data-testid="spec-propose-revision-text"
            />
          </div>

          <div>
            <label
              className="block text-[11px] font-medium text-zinc-700"
              htmlFor="spec-propose-rationale"
            >
              Why is this needed? (optional)
            </label>
            <textarea
              id="spec-propose-rationale"
              className="mt-1 w-full rounded-sm border border-zinc-300 bg-white px-2 py-1.5 text-xs text-zinc-800 outline-none focus:border-zinc-500"
              rows={3}
              placeholder="e.g. 'The spec uses 'temperature sensor array' consistently and the claim should match.'"
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              disabled={isSubmitting}
              data-testid="spec-propose-revision-rationale"
            />
          </div>

          <p className="text-[11px] text-zinc-500">
            After submitting, you&rsquo;ll be taken to{' '}
            {target.kind === 'claim' ? 'Stage 3' : 'Stage 5'} to actually
            make the change. The proposal will appear there as a banner.
          </p>

          {error && (
            <div
              className="rounded-sm border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-700"
              role="alert"
              data-testid="spec-propose-revision-error"
            >
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-4 py-2">
          <button
            type="button"
            className="rounded-sm border border-zinc-300 bg-white px-3 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="spec-propose-revision-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-sm bg-zinc-900 px-3 py-1 text-xs font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="spec-propose-revision-submit"
          >
            {isSubmitting && <Loader2 className="h-3 w-3 animate-spin" />}
            {targetCtaText}
          </button>
        </div>
      </div>
    </div>
  );
}
